import { Calendar, ChevronLeft, ChevronRight } from 'lucide-react';
import { cn } from '../../utils/cn';
import { localDateStr } from '../../utils/formatters';

/**
 * Suma `offset` días a una fecha `YYYY-MM-DD` y devuelve la nueva en el mismo
 * formato, calculada en hora local (idéntico al patrón de liveRoutes/TorreControl).
 */
function shiftDay(dateStr, offset) {
  const [y, m, d] = dateStr.split('-').map(Number);
  return localDateStr(new Date(y, m - 1, d + offset));
}

/**
 * DateNav — selector de DÍA ÚNICO (flechas día±1 + botón Hoy + input date oculto).
 *
 * Primitivo del DS que unifica el `DateNav` interno duplicado en
 * src/pages/liveRoutes/index.jsx y src/views/adminView/torreControl/TorreControl.jsx.
 * Replica fielmente el look original (h-9, bordes segmentados, tabular-nums).
 *
 * Emite un único callback `onChange(nextDateStr)` con la fecha resultante en
 * formato `YYYY-MM-DD`. La aritmética de días se resuelve internamente.
 *
 * @param {{
 *   date: string,                 // fecha seleccionada, 'YYYY-MM-DD'
 *   today?: string,              // 'YYYY-MM-DD' de hoy (default: hoy local)
 *   onChange: (next: string) => void,
 *   max?: string,                // tope superior 'YYYY-MM-DD' (default: today)
 *   t?: (s: string) => string,   // función de i18n opcional (default: identidad)
 *   className?: string,
 * }} props
 */
export function DateNav({
  date,
  today = localDateStr(),
  onChange,
  max = today,
  t,
  className,
}) {
  const tr = typeof t === 'function' ? t : (s) => s;
  const atMax = date >= max;
  const atToday = date === today;

  return (
    <div
      className={cn(
        // shrink-0 + nowrap: la fecha jamás se parte en dos líneas ni el control
        // se comprime cuando la fila de acciones del header queda justa.
        'inline-flex shrink-0 items-center whitespace-nowrap rounded-lg border border-border bg-bg h-9 overflow-hidden',
        className,
      )}
    >
      <button
        type="button"
        onClick={() => onChange(shiftDay(date, -1))}
        className="px-2 h-full text-fg-muted hover:text-fg hover:bg-bg-muted transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-brand-500"
        aria-label={tr('Día anterior')}
      >
        <ChevronLeft className="w-4 h-4" />
      </button>

      <label className="relative inline-flex items-center gap-1.5 px-2 h-full border-l border-r border-border">
        <Calendar className="w-3.5 h-3.5 text-fg-muted" aria-hidden />
        <span className="text-sm font-medium text-fg tabular-nums">{date}</span>
        <input
          type="date"
          value={date}
          max={max}
          onChange={(e) => {
            if (e.target.value && e.target.value <= max) onChange(e.target.value);
          }}
          aria-label={tr('Seleccionar fecha')}
          className="absolute inset-0 opacity-0 cursor-pointer"
        />
      </label>

      <button
        type="button"
        onClick={() => onChange(shiftDay(date, 1))}
        disabled={atMax}
        className="px-2 h-full text-fg-muted hover:text-fg hover:bg-bg-muted transition-colors disabled:opacity-40 disabled:cursor-not-allowed focus:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-brand-500"
        aria-label={tr('Día siguiente')}
      >
        <ChevronRight className="w-4 h-4" />
      </button>

      <button
        type="button"
        onClick={() => onChange(today)}
        disabled={atToday}
        className="px-2.5 h-full text-xs font-medium border-l border-border text-fg-muted hover:text-fg hover:bg-bg-muted disabled:opacity-40 disabled:cursor-not-allowed focus:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-brand-500"
      >
        {tr('Hoy')}
      </button>
    </div>
  );
}

export default DateNav;
